import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { Bool } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { BulkMessagingQueries } from '@frontend/api-bulk-messaging';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { FaxQueries } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { FormsSubmission } from '@frontend/api-forms';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { useListDraftsCountQuery } from '@frontend/api-sms-draft';
import {
  AnalyticsFeatureFlags,
  useHasAppointmentAnalyticsFeature,
  useIsPAEligibleAccount,
  useShowCallIntelNav,
} from '@frontend/analytics';
import { filterLocationIdsByACLs, getUser } from '@frontend/auth-helpers';
import { NavItem } from '@frontend/components';
import { ContactsHooks } from '@frontend/contacts';
import { useFeatureAccess } from '@frontend/feature-access';
import {
  routes as formRoutes,
  routeUtils as formRouteUtils,
  pendoTags as formsPendoTags,
  useFormsSubmissionV2FeatureFlag,
} from '@frontend/forms';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useBulkDraftShallowStore } from '@frontend/messages';
import { useMerchantPayoutsPausingStatus, useShouldShowPayoutsPausingAlert } from '@frontend/payments';
import { useMerchants } from '@frontend/payments-hooks';
import { useHasPhoneSystemAccess } from '@frontend/phone-config';
import { useSchedulingLocationInfo } from '@frontend/schedule';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { DeepReadonly } from '@frontend/types';
import { theme } from '@frontend/theme';

export const usePrimaryNavContent = () => {
  const { t } = useTranslation('primaryNav');

  const hasCallIntelAccess = useShowCallIntelNav();
  const tokens = useTranslationTokens();
  const hasAppointAnalyticsFeature = useHasAppointmentAnalyticsFeature();
  const { isPAEligibleAccount } = useIsPAEligibleAccount();
  const { getCFLocationMap } = useSchedulingLocationInfo();

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const user = getUser();

  // Customization Flags
  const hasPhoneSystemAccess = useHasPhoneSystemAccess({ locationIds: selectedLocationIds });

  // Feature Flags

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const faxBlockFeatureFlag = getFeatureFlagValue('fax-2-0-block');
  const faxArchiveFeatureFlag = getFeatureFlagValue('fax-2-0-archive');
  const ourPartnersPage = getFeatureFlagValue('our-partners-page');
  const autoRecallAnalyticsDisabled = getFeatureFlagValue(AnalyticsFeatureFlags.disableAutoRecallInAnalytics);

  const { canUseFormsSubmissionV2 } = useFormsSubmissionV2FeatureFlag();
  const { hasQuickFillFeature } = QuickFillHooks.useQuickFillFeatureDetails();

  const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');
  const isOnlineSchedulingEnabledForAnyLocation = Object.values(onlineSchedulingCFLocationMap).some((value) => !!value);

  const hasListHomeIconAccess = ContactsHooks.useHasListHomeIconAccess();

  const actionableTabVisibilityFF = getFeatureFlagValue('nwx:inbox-actionable-tab', 'ANY');
  const { data: actionableTabIsEnabled, isLoading: isEnableActionableTabLoading } =
    MessagingSettingsV1.Queries.useListQuery(
      {
        settingType: SettingType_Enum.INBOX_LOCATION_SETTING,
        orgId: selectedOrgId,
        locationIds: selectedLocationIds,
      },
      {
        enabled: !!actionableTabVisibilityFF,
        select: (data) => {
          return data.settings.some(({ setting }) => {
            if ('inboxLocationSetting' in setting) {
              return setting.inboxLocationSetting.enableActionableTab ?? false;
            }
            return false;
          });
        },
      }
    );

  const isActionableFeatureEnabled = actionableTabIsEnabled && actionableTabVisibilityFF;

  // ACLs
  const locationIdsWithInboxAccess = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });

  // Counts
  const { data: inboxUnreadCount } = SMSDataV3.Queries.useListThreadsCountQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationId: locationIdsWithInboxAccess[0] ?? '',
      groupIds: locationIdsWithInboxAccess,
      statuses: isActionableFeatureEnabled ? undefined : [SMSDataV3.Types.KnownThreadStatus.NEW],
      actionable: isActionableFeatureEnabled ? Bool.BOOL_TRUE : Bool.BOOL_UNSPECIFIED,
    },
    options: {
      select: (data) => data.count,
      enabled: !!locationIdsWithInboxAccess.length && !isEnableActionableTabLoading,
    },
  });
  const voicemailsUnreadCount = PhoneCallsQueries.useVoicemailsUnreadCount();
  const { initialYear: initialBulkDraftYear } = useBulkDraftShallowStore('initialYear');
  const bulkDraftYear = initialBulkDraftYear ? Number(initialBulkDraftYear) : new Date().getFullYear();
  const { hasAccess: hasBulkTextAccess } = useFeatureAccess('commx-bulk-messaging', Permission.BULK_TEXTING_MANAGE);
  const { hasAccess: hasBulkEmailAccess } = useFeatureAccess(
    'commx-email-marketing-v2',
    Permission.EMAIL_MARKETING_MANAGE
  );
  const { hasAccess: hasFaxAccess } = useFeatureAccess('fax-2-0', Permission.FAX_INBOX_MANAGE);

  const { isFeatureHiddenInAllLocations: hideReviewsPage } =
    CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
      locationIds: selectedLocationIds,
      enabled: true,
      customizationFlag: Feature.REVIEWS_HOME_ICON,
    });

  const { data: faxUnreadCount } = FaxQueries.useCountFaxes(selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0,
  });
  const { data: faxDraftUnreadCount } = FaxDraftQueries.useGetDraftCount(selectedLocationIds[0], selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0 && !!selectedLocationIds[0],
  });
  const { count: bulkMessageDraftCount } = BulkMessagingQueries.useListCampaignDrafts(
    {
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      year: bulkDraftYear,
    },
    hasBulkEmailAccess,
    hasBulkTextAccess
  );
  const { data: smsDraftCount } = useListDraftsCountQuery(
    {
      orgId: selectedOrgId,
      userId: user?.userID ?? '',
      locationId: locationIdsWithInboxAccess[0] ?? '',
      groupIds: locationIdsWithInboxAccess,
    },
    { enabled: !!locationIdsWithInboxAccess.length }
  );

  const { data: schedulesCount } = ManualSmsScheduledV1.Queries.useListThreadsCountQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationIds: selectedLocationIds,
    },
  });

  const firstLocationId = selectedLocationIds[0];
  const { hasAccountAlert: hasPaymentAccountAlert } = useMerchants(firstLocationId)['firstLocation'];
  const { status: paymentAccountAlertType } = useMerchantPayoutsPausingStatus(firstLocationId);
  const shouldShowPaymentsAccountAlert = useShouldShowPayoutsPausingAlert(firstLocationId);

  const formsSubmissionCount = FormsSubmission.useSubmissionsUnreadCount();

  return [
    {
      iconName: 'home',
      label: t('Home'),
      root: '/home',
      subNavItems: [
        {
          label: t('My Dashboard'),
          relativePath: '/dashboard',
          trackingId: 'app-nav-item-dashboard',
          description: 'A personalized home page and dashboard that surfaces key insights, metrics, and quick actions.',
        },
        {
          label: t('Settings'),
          path: '#settings/organization' as NavItem['subNavItems'][number]['path'],
          show: true,
          trackingId: 'app-nav-item-portal',
        },
        {
          label: t('Our Partners'),
          relativePath: '/partners',
          show: ourPartnersPage,
          trackingId: 'app-nav-item-our-partners',
          description:
            'Showcases integrated partners, third-party services, and potential add-ons available for your account.',
        },
      ],
      show: true,
    },
    {
      hasCount: !!inboxUnreadCount,
      iconName: 'inbox',
      label: t('Messages'),
      root: '/messages',
      subNavItems: [
        {
          count: inboxUnreadCount,
          label: t('Inbox'),
          relativePath: '/inbox',
          trackingId: 'nav-messages-inbox',
          description:
            'Central hub for all incoming text messages and outgoing text messages. View, read, and respond to real-time patient and customer communications.',
        },
        {
          count: schedulesCount?.count,
          isPassiveCount: true,
          label: t('Scheduled'),
          relativePath: '/scheduled',
          trackingId: 'nav-messages-scheduled',
          description:
            'Displays all messages that are set for future delivery, with options to edit or cancel before sending.',
        },
        {
          count: smsDraftCount?.count,
          isPassiveCount: true,
          label: t('Drafts'),
          relativePath: '/drafts',
          trackingId: 'nav-messages-drafts',
          description: 'Holds incomplete or unsent messages. Drafts can be edited, scheduled, or sent immediately.',
        },
        {
          label: t('Archived'),
          relativePath: '/archived',
          trackingId: 'nav-messages-archived',
          description:
            'A read-only storage of older messages that have been removed from the active inbox, preserving past communication history.',
        },
        {
          label: t('Blocked'),
          relativePath: '/blocked',
          trackingId: 'nav-messages-blocked',
          description:
            'Lists blocked contacts and message threads, preventing further incoming messages from specific numbers or addresses.',
        },
      ],
      settings: {
        path: '/messaging',
        label: '',
      },
    },
    {
      hasCount: !!voicemailsUnreadCount,
      iconName: 'phone',
      show: hasPhoneSystemAccess,
      root: '/calls',
      label: t('Calls'),
      subNavItems: [
        {
          label: t('Call History'),
          altSearchLabel: t('Recent Calls'),
          relativePath: '/call-history',
          trackingId: 'nav-calls-recent-calls',
          description:
            'A log of inbound and outbound calls, including caller details, call recordings, and timestamps.',
        },
        {
          count: voicemailsUnreadCount,
          label: t('Voicemails'),
          relativePath: '/voicemails',
          trackingId: 'nav-calls-voicemails',
          description: 'Displays recorded voicemail messages. Users can listen, mark as read, tag, or delete messages.',
        },
        {
          label: t('Call Queue Data'),
          relativePath: '/call-queue-stats',
          trackingId: 'nav-calls-call-queue-data',
          description:
            'Provides call queue metrics like hold times, number of callers in queue, and service performance statistics.',
        },
      ],
      settings: {
        path: '/phone/main',
        label: '',
      },
    },
    {
      iconName: 'calendar',
      label: t('Schedule'),
      root: '/schedule',
      subNavItems: [
        {
          label: t('Calendar'),
          relativePath: '/calendar',
          trackingId: 'nav-schedule-calendar',
          description:
            'Visual scheduling interface for appointments, out of office events, and other time-based events. Offers day view only by provider.',
        },
        {
          label: t('Request History'),
          relativePath: '/request-history',
          show: isOnlineSchedulingEnabledForAnyLocation,
          trackingId: 'nav-schedule-request-history',
          description:
            'Displays historical appointment requests and their statuses. Useful for follow-up and tracking patient inquiries.',
        },
        {
          show: hasQuickFillFeature,
          label: t('Quick Fill History'),
          relativePath: '/quickfill-history',
          trackingId: 'nav-schedule-quickfill-history',
          description:
            'Records the activity and notifications related to filling canceled appointment slots with new patients quickly.',
        },
      ],
      settings: {
        path: '/schedule',
        label: '',
      },
    },
    {
      hasCount: !!hasPaymentAccountAlert && shouldShowPaymentsAccountAlert,
      hasCountColor: paymentAccountAlertType === 'error' ? theme.colors.critical50 : theme.colors.warning50,
      iconName: 'payments',
      label: t('Payments'),
      root: '/payments',
      subNavItems: [
        {
          label: t('Requests'),
          relativePath: '/invoices',
          trackingId: 'nav-payments-invoices',
          description:
            'Lists all issued invoices and payment requests, with options to view details, send to customers, or track payment status.',
        },
        {
          label: t('Payouts'),
          relativePath: '/payouts',
          trackingId: 'nav-payments-payouts',
          description:
            'Shows disbursements/payouts received or pending to your practice account, including settlement dates and amounts.',
        },
        {
          label: t('Payment Plans'),
          relativePath: '/payment-plans',
          trackingId: 'nav-payments-payment-plans',
          description:
            'Create and manage recurring billing plans for patients or clients, including installment schedules and balances.',
        },
      ],
      settings: {
        path: '/payments',
        label: '',
      },
    },
    {
      hasCount: !!formsSubmissionCount.data?.count,
      iconName: 'forms',
      label: t('Forms'),
      root: '/forms',
      subNavItems: [
        {
          count: formsSubmissionCount.data?.count,
          label: t('Submissions'),
          relativePath: '/submissions',
          path: canUseFormsSubmissionV2
            ? `/forms${formRouteUtils.getRoutePathAsString(
                formRoutes.submissionsV2.goTo({
                  listType: 'inbox',
                  subCategory: 'all',
                  relativePath: true,
                })
              )}`
            : undefined,
          trackingId: formsPendoTags.sideNav.submissions,
          description:
            'Displays user-submitted form data. Ideal for reviewing patient intake, consent forms, or survey responses.',
        },
        {
          label: t('My Forms'),
          relativePath: '/library',
          path: `/forms${formRouteUtils.getRoutePathAsString(formRoutes.library.formsV2({ relativePath: true }))}`,
          trackingId: formsPendoTags.sideNav.myForms,
          description:
            'Collection of all created forms in your account. Manage, duplicate, or edit your practice’s custom digital forms.',
        },
        {
          label: t('Kiosk Mode'),
          relativePath: '/kiosk',
          trackingId: formsPendoTags.sideNav.kiosk,
          description: 'A simplified, patient-facing mode for in-office form completion on a tablet or kiosk.',
        },
        {
          label: t('Templates'),
          relativePath: '/templates',
          path: `/forms${formRouteUtils.getRoutePathAsString(
            formRoutes.templates.templatesList({ relativePath: true })
          )}`,
          trackingId: formsPendoTags.sideNav.templates,
          description: 'Library of form templates. Browse, select, and adapt pre-built templates for quick deployment.',
        },
        {
          label: t('Builder'),
          relativePath: '/builder/new',
          trackingId: formsPendoTags.sideNav.builder,
          description: 'Create or customize digital forms from scratch using a drag-and-drop interface.',
        },
        {
          label: t('Request Digitization'),
          relativePath: '/digitization',
          trackingId: formsPendoTags.sideNav.requestDigitization,
          description: 'Request professional conversion of paper or PDF forms into interactive digital versions.',
        },
      ],
      settings: {
        path: '/digital-forms',
        label: '',
        show: true,
      },
    },
    {
      iconName: 'fax',
      label: t('Fax'),
      root: '/fax',
      show: hasFaxAccess,
      hasCount: !!faxUnreadCount?.faxCount,
      subNavItems: [
        {
          count: faxUnreadCount?.faxCount,
          label: t('Fax Inbox'),
          relativePath: '/inbox',
          trackingId: `${FaxPrefixes.Nav}-inbox`,
          description:
            'Central location for all incoming and outgoing faxes. View, tag, archive, and further manage received fax documents in a single interface.',
        },
        {
          label: t('Drafts'),
          relativePath: '/drafts',
          count: faxDraftUnreadCount?.count,
          isPassiveCount: true,
          trackingId: `${FaxPrefixes.Nav}-drafts`,
          description: 'Collection of unsent or paused fax transmissions. Edit details or send these faxes when ready.',
        },

        ...(faxArchiveFeatureFlag
          ? [
              {
                label: t('Archived'),
                relativePath: '/archived',
                count: faxUnreadCount?.archivedFaxCount,
                isPassiveCount: true,
                trackingId: `${FaxPrefixes.Nav}-archived`,
                description:
                  'Archived fax history for record-keeping. Preserves older fax communications outside the main inbox.',
              },
            ]
          : []),
        {
          label: t('Fax Contacts'),
          relativePath: '/contacts',
          trackingId: `${FaxPrefixes.Nav}-contacts`,
          description:
            'Manage contact information specifically for faxing. Add or remove fax numbers and relevant details.',
        },
        ...(faxBlockFeatureFlag
          ? [
              {
                count: faxUnreadCount?.blockedFaxCount,
                isPassiveCount: true,
                label: t('Blocked'),
                relativePath: '/blocked',
                trackingId: `${FaxPrefixes.Nav}-blocked`,
                description: 'List of blocked fax numbers to prevent unwanted inbound or outbound fax traffic.',
              },
            ]
          : []),
        {
          label: t('Send New Fax'),
          relativePath: '/drafts/new',
          show: false,
          trackingId: `${FaxPrefixes.Nav}-send-new-fax`,
          description:
            'Initiate a new fax transmission by uploading documents, selecting recipients, and adding a cover sheet.',
        },
        {
          label: t('Send From Template'),
          relativePath: '/drafts/new-from-template',
          show: false,
          trackingId: `${FaxPrefixes.Nav}-send-from-template`,
          description:
            'Quickly prepare a new fax using predefined templates to streamline repetitive or common transmissions.',
        },
      ],
      settings: {
        path: '/fax',
        label: '',
      },
    },
    {
      iconName: 'contacts',
      label: tokens.contacts,
      root: '/contacts',
      subNavItems: [
        {
          label: tokens.allContacts,
          relativePath: '/all-contacts',
          trackingId: 'contact-2.0-list-menu-all',
          description: 'Displays the entire patient or client contact list, with searching and filtering capabilities.',
        },
        {
          label: t('Lists'),
          relativePath: '/lists',
          trackingId: 'contact-2.0-list-menu-lists',
          description: 'View collections lists, birthdays, and overdue patients, useful for targeted messaging.',
          show: hasListHomeIconAccess,
        },
      ],
    },
    {
      iconName: 'reviews-dash',
      label: t('Reviews'),
      root: '/reviews',
      subNavItems: [
        {
          label: t('All Reviews'),
          relativePath: '/all-reviews',
          trackingId: 'nav-reviews-all-reviews',
          description:
            'Aggregates all online reviews across multiple platforms for a unified view and easy management.',
        },
        {
          label: t('Google'),
          relativePath: '/google',
          trackingId: 'nav-reviews-google',
          description: 'Filters reviews collected specifically from Google, allowing for engagement and management.',
        },
        {
          label: t('Facebook'),
          relativePath: '/facebook',
          trackingId: 'nav-reviews-facebook',
          description: 'Displays reviews and recommendations sourced from Facebook pages associated with the practice.',
        },
        {
          label: t('Private'),
          relativePath: '/private',
          trackingId: 'nav-reviews-private',
          description: 'Confidential feedback or private reviews gathered internally, not visible on public platforms.',
        },
      ],
      settings: {
        path: '/reviews',
        label: '',
        show: true,
      },
      show: !hideReviewsPage,
    },
    {
      iconName: 'analytics',
      label: t('Analytics'),
      root: '/analytics',
      subNavItems: [
        {
          label: t('Overview'),
          relativePath: '/dashboard',
          trackingId: 'nav-analytics-overview',
          description:
            'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.',
        },
        {
          label: t('Practice'),
          relativePath: '/practice/main',
          show: isPAEligibleAccount,
          trackingId: 'nav-analytics-practice',
          description:
            'In-depth metrics about the practice’s overall performance, including opportunities, patient provider production, and more.',
        },
        {
          label: t('Call Intelligence'),
          relativePath: '/call-intelligence/main',
          show: hasCallIntelAccess,
          trackingId: 'nav-analytics-call-intelligence',
          description: 'Detailed data on call performance, sentiment, call durations, and other telephony insights.',
        },
        {
          label: t('Auto Recall'),
          relativePath: '/auto-recall',
          show: !autoRecallAnalyticsDisabled,
          trackingId: 'nav-analytics-auto-recall',
          description:
            'Metrics related to the automated recall system, tracking outreach effectiveness and follow-up responses.',
        },
        {
          label: t('Phone'),
          relativePath: '/phone',
          trackingId: 'nav-analytics-phone',
          description: 'Analytics focused on phone usage patterns, volume, and trends for better resource management.',
        },
        {
          label: t('Messaging'),
          relativePath: '/messaging',
          trackingId: 'nav-analytics-messaging',
          description: 'Reports on message volume, response rates, and overall engagement via text or chat channels.',
        },
        {
          label: t('Appointment'),
          relativePath: '/appointment',
          show: hasAppointAnalyticsFeature,
          trackingId: 'nav-analytics-appointments',
          description: 'Tracks appointment-related data, such as scheduling trends, cancellations, and no-show rates.',
        },
        {
          label: t('Reviews'),
          relativePath: '/reviews',
          show: !hideReviewsPage,
          trackingId: 'nav-analytics-reviews',
          description:
            'Tracks patient or customer sentiment over time, analyzing both quantity and quality of reviews.',
        },
      ],
    },
    {
      iconName: 'bulk-messaging',
      label: t('Marketing'),
      root: '/bulk',
      show: true,
      subNavItems: [
        {
          label: t('All Campaigns'),
          relativePath: '/all-campaigns/email',
          trackingId: 'nav-marketing-all-campaings',
          description:
            'Lists all email and text marketing campaigns, both active and historical, with performance details for each.',
        },
        {
          label: t('Drafts'),
          relativePath: '/drafts',
          count: bulkMessageDraftCount,
          isPassiveCount: true,
          trackingId: 'nav-marketing-drafts',
          description: 'Shows text marketing campaigns still in draft form, allowing further edits before launch.',
        },
      ],
    },
  ] as const satisfies DeepReadonly<NavItem[]>;
};
